import moment from 'moment'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FlightSegment, FlightsFareTypes } from 'constants/flight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

/**
 * Stable key for a flight search segment. Return fares use the legacy
 * DEPARTING / RETURNING keys; every other fare type (one-way, multi-city) keys
 * each leg positionally as `flight-${index}`.
 */
export function getSegmentKey(index: number, isReturnFare: boolean): string {
  if (isReturnFare) {
    return index === 0 ? FlightSegment.DEPARTING : FlightSegment.RETURNING
  }

  return `flight-${index}`
}

export function getSegmentKeys(length: number, isReturnFare: boolean): Array<string> {
  return Array.from({ length }, (_, index) => getSegmentKey(index, isReturnFare))
}

/**
 * Build the search itinerary from the widget's flight inputs, or `undefined`
 * when the inputs are incomplete. Multi-city maps every leg (and requires all of
 * them to be complete); return appends the reversed leg; one-way is a single leg.
 */
export function getSearchItinerary(
  flights: Array<FlightSearchWidgetFlightItem>,
  fareType: FlightsFareTypes | string | undefined,
): FlightSearchParams['itinerary'] | undefined {
  const firstFlight = flights[0]
  if (!firstFlight?.departureAirport?.airportCode || !firstFlight.arrivalAirport?.airportCode || !firstFlight.checkinDate) {
    return undefined
  }

  if (fareType === FlightsFareTypes.MULTI_CITY) {
    const itinerary = flights.map((flight) => {
      if (!flight.departureAirport?.airportCode || !flight.arrivalAirport?.airportCode || !flight.checkinDate) {
        return undefined
      }

      return {
        departureAirport: flight.departureAirport.airportCode,
        arrivalAirport: flight.arrivalAirport.airportCode,
        departureDate: moment(flight.checkinDate).format(ISO_DATE_FORMAT),
      }
    }).filter((flight): flight is FlightSearchParams['itinerary'][number] => !!flight)

    return itinerary.length === flights.length ? itinerary : undefined
  }

  const itinerary = [{
    departureAirport: firstFlight.departureAirport.airportCode,
    arrivalAirport: firstFlight.arrivalAirport.airportCode,
    departureDate: moment(firstFlight.checkinDate).format(ISO_DATE_FORMAT),
  }]

  if (fareType === FlightsFareTypes.RETURN && firstFlight.checkoutDate) {
    itinerary.push({
      departureAirport: firstFlight.arrivalAirport.airportCode,
      arrivalAirport: firstFlight.departureAirport.airportCode,
      departureDate: moment(firstFlight.checkoutDate).format(ISO_DATE_FORMAT),
    })
  }

  return itinerary
}
